import { useEffect } from "react";
import { Box, Button, Paper } from "@mui/material";
import MechanicsForm from "./../../components/Mechanics.tsx";
import ArrangementForm from "./Arrangement.tsx";
import DutTestpointTable from "./DutTestpointTable.tsx";
import DutToZeroMechanicsForm from "./DutToZeroMechanics.tsx";
import DxfViewer from "./DxfViewer.tsx";
import { useDxf } from "../../hooks/useDxf.ts";
import { useMechanicsConfiguration } from "../../context/MechanicsConfigurationContext.tsx";
import { useElectronicsConfiguration } from "../../context/ElectronicsConfigurationContext.tsx";
import { useTestpointSignals } from "../../hooks/useTestpointSignals.ts";
import { ElectronicsConfiguration } from "../../models/electronicsConfiguration.ts";
import { getHousingInfo } from "../../utils/housingDetails.ts";
import PageLayout from '../../components/PageLayout.tsx';
import { pruneElectronicsReferences } from "../../utils/pruneElectronicsReferences.ts";
import isEqual from "fast-deep-equal";

const MechanicsConfigurationPage = () => {
  const { config, setConfig, handleChange } = useMechanicsConfiguration();
  const { config: electronicsConfig, setConfig: setElectronicsConfig } = useElectronicsConfiguration();
  const { getTestpointSignals } = useTestpointSignals();
  const { content: dxf } = useDxf(config);

  useEffect(() => {
    const fetchSignals = async () => {
      let signals = await getTestpointSignals(config);
      if (signals === null) {
        signals = [];
      }

      const updatedConfig: ElectronicsConfiguration = pruneElectronicsReferences(
        {
          ...electronicsConfig,
          max_modules: getHousingInfo(config.mechanics)?.maxModules ?? 0,
        },
        signals,
      );
      if (!isEqual(updatedConfig, electronicsConfig)) {
        setElectronicsConfig(updatedConfig);
      }
    };

    fetchSignals();
  }, [
    electronicsConfig,
    getTestpointSignals,
    config,
    setElectronicsConfig,
  ]);

  return (
    <PageLayout
      title="Mechanik"
      backText="Zurück zum Projekt"
      backTarget="/project"
      nextText="Weiter zur Elektronik"
      nextTarget="/electronics"
    >
      <Paper sx={{ p: 3, mb: 6 }}>
        <MechanicsForm
          data={config.mechanics}
          showDutSize={true}
          onChange={(field, value) => handleChange("mechanics", { ...config.mechanics, [field]: value }) }
        />
        <Box mt={2} />
        <ArrangementForm
          data={config.arrangement}
          onChange={(field, value) => handleChange("arrangement", { ...config.arrangement, [field]: value }) }
        />
      </Paper>
      <Paper sx={{ p: 3, mb: 6 }}>
        <DxfViewer content={dxf} />
        <Box mt={2} display="flex" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              if (!dxf) return;
              const blob = new Blob([dxf], { type: "application/dxf" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "mechanik.dxf";
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download dxf-Datei
          </Button>
        </Box>
      </Paper>
      <Paper sx={{ p: 3, mb: 6 }}>
        <DutTestpointTable
          data={config.testpoints}
          onChange={(testpoints) => { setConfig({ ...config, testpoints: [...testpoints] }); } }
        />
        <Box mt={2} />
        <DutToZeroMechanicsForm
          data={config.dut_to_zero_mechanics}
          onChange={(field, value) =>
            handleChange("dut_to_zero_mechanics", { ...config.dut_to_zero_mechanics, [field]: value })
          }
        />
      </Paper>
    </PageLayout>
  );
};

export default MechanicsConfigurationPage;
